import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { Button, Input } from "reactstrap";
import { OPENAI_EMAIL_FORM } from "../../../../constants/modal.constant";
import { useEmailCore } from "../../../../cores/email";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { SocialTemplate } from "../../../../cores/openAi/interface/api/get-social-templates";
import { openAiApiService } from "../../../../cores/openAi/services/api";
import { addSign } from "../../../utils/signUtils";
import "./EmailAiWriterModal.scss";

interface Props {
  recipientNames?: string[];
}

type EmailLength = "short" | "medium" | "long";
type ApplyMode = "replace" | "append";

const stripHtml = (value: string) => {
  if (!value) return "";
  const element = document.createElement("div");
  element.innerHTML = value;
  return (element.textContent || element.innerText || "").trim();
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const emailBodyToHtml = (template: SocialTemplate & { callToAction?: string }) => {
  const body = String(template.body || "").trim();
  const callToAction = String(template.callToAction || "").trim();
  const completeBody =
    callToAction && !body.toLowerCase().includes(callToAction.toLowerCase())
      ? `${body}\n\n${callToAction}`
      : body;

  return completeBody
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

export default function EmailAiWriterModal({ recipientNames = [] }: Props) {
  const intl = useIntl();
  const { company, sign: selectedSign } = useMeCore();
  const { subject: currentSubject, message, setSubject, setMessage } =
    useEmailCore();
  const { turnOffModal } = useGlobalCore();
  const [purpose, setPurpose] = useState(currentSubject || "");
  const [audience, setAudience] = useState(recipientNames.join(", "));
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [tone, setTone] = useState("professional-friendly");
  const [length, setLength] = useState<EmailLength>("medium");
  const [emoticons, setEmoticons] = useState(false);
  const [useCurrentDraft, setUseCurrentDraft] = useState(Boolean(stripHtml(message)));
  const [applyMode, setApplyMode] = useState<ApplyMode>("replace");
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<
    Array<SocialTemplate & { callToAction?: string }>
  >([]);
  const [selected, setSelected] = useState(0);

  const toneOptions = useMemo(
    () => [
      {
        value: "professional-friendly",
        label: intl.formatMessage({ id: "app.emailAi.toneProfessional" }),
      },
      {
        value: "warm",
        label: intl.formatMessage({ id: "app.emailAi.toneWarm" }),
      },
      {
        value: "direct",
        label: intl.formatMessage({ id: "app.emailAi.toneDirect" }),
      },
      {
        value: "persuasive",
        label: intl.formatMessage({ id: "app.emailAi.tonePersuasive" }),
      },
      {
        value: "formal",
        label: intl.formatMessage({ id: "app.emailAi.toneFormal" }),
      },
    ],
    [intl]
  );

  const selectedTemplate = templates[selected];
  const canGenerate = Boolean(company?.id && purpose.trim() && !loading);

  const mapGenerationError = useCallback(
    (error: any) => {
      const status = error?.response?.status || error?.request?.status;
      if (status === 403) {
        return intl.formatMessage({ id: "app.emailAi.errorForbidden" });
      }
      if (status === 504) {
        return intl.formatMessage({ id: "app.emailAi.errorTimeout" });
      }
      return (
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({ id: "app.emailAi.errorGeneric" })
      );
    },
    [intl]
  );

  const generate = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!canGenerate) return;

      setLoading(true);
      try {
        const selectedTone =
          toneOptions.find((option) => option.value === tone)?.label || tone;
        const response = await openAiApiService.getEmailPosts(company!.id, {
          industry: company?.name || "",
          subject: purpose.trim(),
          audience: audience.trim(),
          tone: selectedTone,
          length,
          emoticons,
          hashtags: false,
          additionalInfo: additionalInfo.trim(),
          currentDraft: useCurrentDraft ? stripHtml(message) : "",
          socialMedias: ["Email"],
        });

        if (!response.length) {
          throw new Error(intl.formatMessage({ id: "app.emailAi.errorEmpty" }));
        }
        setTemplates(response);
        setSelected(0);
      } catch (error: any) {
        toast.error(mapGenerationError(error));
      } finally {
        setLoading(false);
      }
    },
    [
      additionalInfo,
      audience,
      canGenerate,
      company,
      emoticons,
      intl,
      length,
      mapGenerationError,
      message,
      purpose,
      tone,
      toneOptions,
      useCurrentDraft,
    ]
  );

  const applyTemplate = useCallback(() => {
    if (!selectedTemplate) return;

    const generatedHtml = emailBodyToHtml(selectedTemplate);
    setSubject(selectedTemplate.subject || currentSubject);
    if (applyMode === "append" && message) {
      const contentWithGenerated =
        selectedSign?.sign && message.includes(selectedSign.sign)
          ? message.replace(
              selectedSign.sign,
              `<p><br></p>${generatedHtml}${selectedSign.sign}`
            )
          : `${message}<p><br></p>${generatedHtml}`;
      setMessage(contentWithGenerated);
    } else {
      setMessage(
        selectedSign ? addSign(generatedHtml, selectedSign) : generatedHtml
      );
    }
    turnOffModal(OPENAI_EMAIL_FORM);
    toast.success(intl.formatMessage({ id: "app.emailAi.applied" }));
  }, [
    applyMode,
    currentSubject,
    intl,
    message,
    selectedSign,
    selectedTemplate,
    setMessage,
    setSubject,
    turnOffModal,
  ]);

  return (
    <div className="email-ai-writer">
      <header className="email-ai-writer__header">
        <span className="email-ai-writer__icon">
          <FontAwesomeIcon icon={["fas", "magic"]} />
        </span>
        <div>
          <h2>{intl.formatMessage({ id: "app.emailAi.title" })}</h2>
          <p>{intl.formatMessage({ id: "app.emailAi.subtitle" })}</p>
        </div>
      </header>

      <form className="email-ai-writer__form" onSubmit={generate}>
        <label>
          <span>{intl.formatMessage({ id: "app.emailAi.purpose" })} *</span>
          <Input
            value={purpose}
            onChange={(event) => setPurpose(event.target.value)}
            placeholder={intl.formatMessage({
              id: "app.emailAi.purposePlaceholder",
            })}
          />
        </label>

        <div className="email-ai-writer__grid">
          <label>
            <span>{intl.formatMessage({ id: "app.emailAi.audience" })}</span>
            <Input
              value={audience}
              onChange={(event) => setAudience(event.target.value)}
              placeholder={intl.formatMessage({
                id: "app.emailAi.audiencePlaceholder",
              })}
            />
          </label>
          <label>
            <span>{intl.formatMessage({ id: "app.emailAi.tone" })}</span>
            <Input
              type="select"
              value={tone}
              onChange={(event) => setTone(event.target.value)}
            >
              {toneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </label>
        </div>

        <label>
          <span>{intl.formatMessage({ id: "app.emailAi.details" })}</span>
          <Input
            type="textarea"
            rows={4}
            value={additionalInfo}
            onChange={(event) => setAdditionalInfo(event.target.value)}
            placeholder={intl.formatMessage({
              id: "app.emailAi.detailsPlaceholder",
            })}
          />
        </label>

        <fieldset>
          <legend>{intl.formatMessage({ id: "app.emailAi.length" })}</legend>
          <div className="email-ai-writer__segments">
            {(["short", "medium", "long"] as EmailLength[]).map((value) => (
              <button
                key={value}
                type="button"
                className={length === value ? "active" : ""}
                onClick={() => setLength(value)}
              >
                {intl.formatMessage({ id: `app.emailAi.length.${value}` })}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="email-ai-writer__checks">
          <label>
            <Input
              type="checkbox"
              checked={emoticons}
              onChange={(event) => setEmoticons(event.target.checked)}
            />
            {intl.formatMessage({ id: "app.emailAi.emojis" })}
          </label>
          {Boolean(stripHtml(message)) && (
            <label>
              <Input
                type="checkbox"
                checked={useCurrentDraft}
                onChange={(event) => setUseCurrentDraft(event.target.checked)}
              />
              {intl.formatMessage({ id: "app.emailAi.improveDraft" })}
            </label>
          )}
        </div>

        <Button color="primary" block disabled={!canGenerate}>
          <FontAwesomeIcon
            icon={["fas", loading ? "spinner" : "magic"]}
            spin={loading}
            className="mr-2"
          />
          {intl.formatMessage({
            id: loading ? "app.emailAi.generating" : "app.emailAi.generate",
          })}
        </Button>
      </form>

      {selectedTemplate && (
        <section className="email-ai-writer__results">
          <div className="email-ai-writer__result-nav">
            <strong>{intl.formatMessage({ id: "app.emailAi.alternatives" })}</strong>
            <div>
              {templates.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={selected === index ? "active" : ""}
                  onClick={() => setSelected(index)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
          <article>
            <span>{intl.formatMessage({ id: "app.sendEmail.subject" })}</span>
            <h3>{selectedTemplate.subject}</h3>
            <p>{selectedTemplate.body}</p>
            {selectedTemplate.callToAction && (
              <strong>{selectedTemplate.callToAction}</strong>
            )}
          </article>

          {message && (
            <div className="email-ai-writer__apply-mode">
              <label>
                <Input
                  type="radio"
                  checked={applyMode === "replace"}
                  onChange={() => setApplyMode("replace")}
                />
                {intl.formatMessage({ id: "app.emailAi.replace" })}
              </label>
              <label>
                <Input
                  type="radio"
                  checked={applyMode === "append"}
                  onChange={() => setApplyMode("append")}
                />
                {intl.formatMessage({ id: "app.emailAi.append" })}
              </label>
            </div>
          )}

          <Button color="success" block onClick={applyTemplate}>
            {intl.formatMessage({ id: "app.emailAi.use" })}
          </Button>
        </section>
      )}
    </div>
  );
}
